<template>
  <div>
    <p>{{ $t("节点配置") }}</p>

    <div v-for="(item, index) in formData.infos" :key="index">
      <t-form-item>
        <t-card :value="`${index}`">
          <div class="infoRow">
            <t-form-item
              :name="`infos[${index}].name`"
              :status-icon="false"
              :rules="requireRule"
              class="infoField"
            >
              <template #label>
                <text-with-mark :text="$t('信息内容')" />
              </template>
              <t-input
                v-model="formData.infos[index].name"
                maxlength="50"
                @blur="handleNameBlur(index)"
              />
            </t-form-item>
            <t-form-item
              :name="`infos[${index}].en_name`"
              :status-icon="false"
              :rules="enNameRule"
              class="infoField"
            >
              <template #label>
                <div
                  style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                  "
                >
                  <span style="color: #6d778c">{{ $t("参数名（Key）") }}</span>
                  <CloseCircleIcon
                    :disabled="formData.infos.length <= 1"
                    size="16"
                    style="padding-left: 8px; vertical-align: sub"
                    @click="deleteInfo(index)"
                  />
                </div>
              </template>
              <t-input
                v-model="formData.infos[index].en_name"
                maxlength="50"
                @change="markEnNameTouched(index)"
                @blur="validateEnNameField(index)"
              />
            </t-form-item>
          </div>
          <t-form-item :name="`infos[${index}].desc`" :status-icon="false">
            <template #label>
              <span style="color: #6d778c">{{ $t("信息描述") }}</span>
            </template>
            <t-textarea
              v-model="formData.infos[index].desc"
              class="prompt-resizable-textarea"
              :placeholder="$t('帮助大模型更好理解信息描述，1000 字以内')"
              maxlength="1000"
            />
          </t-form-item>
          <t-form-item :name="`infos[${index}].askMode`" :status-icon="false">
            <p style="margin-right: 14px">
              <text-with-mark :text="$t('询问方式')" />
            </p>
            <t-radio-group
              v-model="formData.infos[index].askMode"
              @change="onAskModeChange(index, $event)"
            >
              <t-radio :value="AskMode.AI">{{ $t("AI智能询问") }}</t-radio>
              <t-radio :value="AskMode.FIXED">{{ $t("固定话术询问") }}</t-radio>
            </t-radio-group>
          </t-form-item>

          <!-- 固定话术询问 -->
          <template v-if="formData.infos[index].askMode === AskMode.FIXED">
            <t-form-item
              :name="`infos[${index}].fix_ask.content`"
              :status-icon="false"
              :rules="requireRule"
            >
              <t-input
                v-model="formData.infos[index].fix_ask.content"
                maxlength="200"
                style="width: 385px"
              />
            </t-form-item>

            <!-- 选项列表 -->
            <div
              v-if="formData.infos[index].fix_ask.items"
              style="margin-top: 20px"
            >
              <div
                v-for="(item, itemIndex) in formData.infos[index].fix_ask.items"
                :key="itemIndex"
                style="padding: 0px 24px 20px 0"
              >
                <t-form-item
                  :name="`infos[${index}].fix_ask.items[${itemIndex}]`"
                  :rules="requireRule"
                  :required-mark="false"
                >
                  <t-space style="display: flex; align-items: center">
                    <t-input
                      v-model="formData.infos[index].fix_ask.items[itemIndex]"
                      maxlength="200"
                      style="width: 345px"
                      :placeholder="$t('请输入选项内容')"
                    />
                    <CloseCircleIcon
                      size="16"
                      style="cursor: pointer"
                      @click="deleteChooseItem(index, itemIndex)"
                    />
                  </t-space>
                </t-form-item>
              </div>
              <t-form-item
                v-if="(formData.infos[index].fix_ask.items ?? []).length < 20"
                :status-icon="false"
              >
                <t-space size="small">
                  <t-link
                    theme="primary"
                    hover="color"
                    @click="addChooseItem(index)"
                    >{{ $t("添加选项") }}</t-link
                  >
                </t-space>
              </t-form-item>
            </div>
          </template>

          <!-- <t-form-item :name="`infos[${index}].required`" :status-icon="false">
            <t-checkbox v-model="formData.infos[index].required">{{
              $t("设置该信息为用户必填项")
            }}</t-checkbox>
          </t-form-item> -->
        </t-card>
      </t-form-item>
    </div>
    <t-form-item v-if="formData.infos.length < 10" :status-icon="false">
      <t-space size="small">
        <t-link theme="primary" hover="color" @click="addInfo">{{
          $t("添加信息")
        }}</t-link>
      </t-space>
    </t-form-item>
    <t-form-item
      name="infos"
      :rules="infoListRules"
      label-width="0"
      :status-icon="false"
    >
    </t-form-item>
  </div>
</template>

<script lang="ts" setup>
import { getCurrentInstance, nextTick, reactive, watch } from "vue";
import { CloseCircleIcon } from "tdesign-icons-vue-next";
import { FormInstanceFunctions } from "tdesign-vue-next";
import { trim } from "lodash-es";
import { t } from "@/utils/i18n";
import { AskMode, InfoExtractNodeData } from "@/types/infoExtractNodeType";
import { getUUID } from "@/utils";

const emit = defineEmits(["updateData"]);

const props = defineProps<{
  data: InfoExtractNodeData;
}>();
const currentInstance = getCurrentInstance();

const formData = reactive<InfoExtractNodeData>({
  infos: (props.data.infos ?? []).map((item) => ({
    ...item,
    en_name: typeof item.en_name === "string" ? item.en_name : "",
  })),
});

const keepEnNameInfoIds = new Set(
  (props.data.infos ?? [])
    .filter((item) => Object.prototype.hasOwnProperty.call(item, "en_name"))
    .map((item) => item.id),
);

const infoListRules = [
  {
    message: t("不能为空"),
    validator(v) {
      return v.length >= 1;
    },
  },
];

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

const enNamePattern = /^[A-Za-z][A-Za-z0-9_]{0,49}$/;
const enNameErrorMessage = t(
  "格式不正确",
);
const enNameDuplicateErrorMessage = t("参数名（Key）不能重复");
const isDuplicateEnName = (enName: unknown) => {
  if (typeof enName !== "string" || enName === "") {
    return false;
  }
  return formData.infos.filter((item) => item.en_name === enName).length > 1;
};
const enNameRule = [
  {
    validator: (str) => {
      if (str === undefined || str === null || str === "") {
        return true;
      }
      return typeof str === "string" && enNamePattern.test(str);
    },
    message: enNameErrorMessage,
    trigger: "blur",
  },
  {
    validator: (str) => !isDuplicateEnName(str),
    message: enNameDuplicateErrorMessage,
    trigger: "blur",
  },
];

const getParentFormRef = (): FormInstanceFunctions | null => {
  let parent = currentInstance?.parent;
  while (parent) {
    const setupState = (parent as any).setupState;
    const exposed = (parent as any).exposed;
    const refs = (parent as any).refs;
    const formRef = setupState?.formRef || exposed?.formRef || refs?.formRef;

    if (formRef && typeof formRef.validate === "function") {
      return formRef as FormInstanceFunctions;
    }
    parent = parent.parent;
  }
  return null;
};

const getEnNameFields = () => {
  return (formData.infos ?? []).map((_, index) => `infos[${index}].en_name`);
};

const validateEnNameFields = async () => {
  const formRef = getParentFormRef();
  const fields = getEnNameFields();
  if (!formRef || !fields.length) return;

  await nextTick();
  await formRef.validate({
    fields,
    trigger: "all",
    showErrorMessage: true,
  });
};

const validateEnNameField = async (index: number) => {
  if (index < 0) return;
  await validateEnNameFields();
};

const handleNameBlur = async (index: number) => {
  const infoItem = formData.infos[index];
  if (!infoItem || infoItem.en_name) return;

  const trimmedName = trim(infoItem.name);
  if (!enNamePattern.test(trimmedName)) return;

  infoItem.en_name = trimmedName;
  await validateEnNameFields();
};

const markEnNameTouched = (index: number) => {
  const infoId = formData.infos[index]?.id;
  if (infoId) {
    keepEnNameInfoIds.add(infoId);
  }
};

const serializeInfoItem = (item) => {
  const nextItem = {
    ...item,
    en_name: typeof item.en_name === "string" ? item.en_name : "",
  };

  if (!keepEnNameInfoIds.has(item.id) && nextItem.en_name === "") {
    delete nextItem.en_name;
  }

  return nextItem;
};

const addInfo = () => {
  if (formData.infos.length >= 10) return;
  const infoId = `info-${getUUID()}`;
  keepEnNameInfoIds.add(infoId);
  formData.infos.push({
    id: infoId,
    name: "",
    en_name: "",
    desc: "",
    required: true,
    askMode: AskMode.AI,
    fix_ask: {
      content: "",
      items: [],
    },
  });
};

const deleteInfo = (index: number) => {
  if (formData.infos.length <= 1) return;
  formData.infos.splice(index, 1);
};

const deleteChooseItem = (index: number, itemIndex: number) => {
  if (!formData.infos[index].fix_ask?.items) return;
  formData.infos[index].fix_ask.items.splice(itemIndex, 1);
};

const addChooseItem = (index: number) => {
  if (!formData.infos[index].fix_ask) {
    formData.infos[index].fix_ask = {
      content: "",
      items: [],
    };
  }
  if (!formData.infos[index].fix_ask.items) {
    formData.infos[index].fix_ask.items = [];
  }
  formData.infos[index].fix_ask.items.push("");
};

const onAskModeChange = (index: number, value: AskMode) => {
  formData.infos[index].askMode = value;
  if (value === AskMode.FIXED) {
    // 切换到固定话术时，初始化fix_ask结构
    formData.infos[index].fix_ask = {
      content: "",
      items: [""],
    };
  } else {
    // 切换到AI智能询问时，清空fix_ask
    formData.infos[index].fix_ask = {
      content: "",
      items: [],
    };
  }
};

watch(
  formData,
  (newVal) => {
    emit("updateData", {
      ...newVal,
      infos: (newVal.infos ?? []).map((item) => serializeInfoItem(item)),
    });
  },
  {
    immediate: true,
  },
);
</script>
<style lang="less" scoped>
:deep(.flow-t-card) {
  width: 100%;
  background-color: #f9fafc;
  margin-bottom: 15px;
  border-radius: 8px;
  border: 1px solid #e4e8ee;
  :deep(.flow-t-card__header) {
    padding: 0px !important;
  }
}
:deep(.flow-t-button--variant-text:hover) {
  border-color: transparent;
  background-color: transparent;
}
:deep(.flow-t-collapse-panel__wrapper .flow-t-collapse-panel__content) {
  padding-left: 16px;
  padding-top: 0px;
}
:deep(.flow-t-collapse.flow-t--border-less .flow-t-collapse-panel__body) {
  background-color: #f9fafc;
}
.marginBtm {
  margin-bottom: 8px;
}
:deep(.flow-t-form__item.infoField) {
  flex: 1;
  min-width: 0;
}
.infoRow {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
:deep(.flow-t-form__label) {
  padding-right: 0px;
}
:deep(.flow-t-form__controls .flow-t-form__item) {
  margin-right: 0px;
  .flow-t-input__extra {
    position: unset;
  }
}
.prompt-resizable-textarea {
  :deep(.flow-t-textarea__inner) {
    min-height: 55px;
    max-height: 900px;
    resize: vertical;
  }
}
.collapseLabel {
  display: flex;
  justify-content: space-between;
}
@media (max-width: 640px) {
  .infoRow {
    flex-direction: column;
    gap: 0;
  }
}
</style>
